// @flow

import React, {useEffect, useState} from 'react';
import {connect} from 'react-redux';

import {getContentType} from 'app/utils/content-type';
import {fetchKBCategoriesForTree} from 'app/kbs/actions';
import type {StoreShapeType} from 'app/reducers/types';
import {denormalizeKbCategoryList} from 'app/kbs/datafetchers';
import type {StoredResponseType} from 'app/utils/redux_v2';
import {gt} from 'app/utils/i18n';

import type {KbCategoryType, KnowledgeBaseType} from 'app/kbs/types';
import {KBTreeCollapsibleItemStructure} from './KBTreeStructures';
import {KBTreeDeleteItemContextProvider} from './KBTreeDeleteItemModal';
import {KBTreeExpandContextProvider} from 'app/kbs/components/KBEditPage/KBTree/KBTreeExpandContext';
import {
  KBTreeEditCategory,
  KBTreeCreateCategoryItem,
  KBTreeCategoryItem,
} from './KBTreeCategoryItem';

const kbCt = getContentType('knowledgebase');

type OwnProps = {|
  kb: KnowledgeBaseType,
|};

type StateProps = {|
  categories: StoredResponseType<KbCategoryType>,
|};

type DispatchProps = {|
  fetchKBCategoriesForTree: AsyncAction<
    typeof fetchKBCategoriesForTree,
    Promise<KbCategoryType>
  >,
|};

type Props = {|
  ...OwnProps,
  ...StateProps,
  ...DispatchProps,
|};

const BaseKBTree = ({kb, fetchKBCategoriesForTree, categories}: Props) => {
  const [expanded, setExpanded] = useState<boolean>(true);
  const [showingWelcomeMessage, showWelcomeMessage] = useState<boolean>(true);
  const fetchCategories = () => fetchKBCategoriesForTree(kb.id, '');
  useEffect(() => {
    fetchCategories();
  }, [kb.id]);

  function toggleAndLoad() {
    if (!expanded) {
      fetchCategories();
    }
    setExpanded(!expanded);
  }

  return (
    <KBTreeDeleteItemContextProvider>
      <KBTreeExpandContextProvider>
        <KBTreeCollapsibleItemStructure
          onToggleExpand={toggleAndLoad}
          icon={kbCt.icon}
          title={kb.name}
          expanded={expanded}
          loading={categories.loading}
        >
          {categories.count > 0 ? (
            <div>
              {categories.results.map((category) => (
                <KBTreeCategoryItem
                  kb={kb}
                  category={category}
                  key={category.id}
                  afterAction={fetchCategories}
                />
              ))}
              <KBTreeCreateCategoryItem
                kb={kb}
                parentCategoryURL={null}
                afterAction={fetchCategories}
              />
            </div>
          ) : showingWelcomeMessage ? (
            <div className="text-center">
              <div className="lead mb-2">
                {gt('Start off your Knowledge Base creating a category')}
              </div>
              <button
                onClick={() => showWelcomeMessage(false)}
                className="btn btn-primary"
              >
                {gt('Create Category')}
              </button>
            </div>
          ) : (
            <KBTreeEditCategory
              onCancel={() => showWelcomeMessage(true)}
              parentCategoryURL={null}
              kb={kb}
              afterAction={fetchCategories}
            />
          )}
        </KBTreeCollapsibleItemStructure>
      </KBTreeExpandContextProvider>
    </KBTreeDeleteItemContextProvider>
  );
};

const mapStateToProps = (state, {kb}): StateProps => ({
  categories: denormalizeKbCategoryList(state, {
    kb_id: kb.id,
    parent_category_id: '',
  }),
});

const mapDispatchToProps = {fetchKBCategoriesForTree};

export const KBTree = connect<
  Props,
  OwnProps,
  StateProps,
  DispatchProps,
  StoreShapeType,
  _
  // $FlowFixMe - flow doesn't understand redux-thunk magic
>(
  mapStateToProps,
  mapDispatchToProps
)(BaseKBTree);
